/** Intl-based money/percent/number formatters used across the app. */

export function formatUSD(n: number, opts?: Intl.NumberFormatOptions): string {
  const maximumFractionDigits = opts?.maximumFractionDigits ?? 2;
  const minimumFractionDigits =
    opts?.minimumFractionDigits ?? Math.min(2, maximumFractionDigits);
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    ...opts,
    minimumFractionDigits,
    maximumFractionDigits
  }).format(n);
}

export function formatPct(n: number, opts?: { signed?: boolean; digits?: number }): string {
  const digits = opts?.digits ?? 2;
  const signed = opts?.signed ?? true;
  const formatted = (n * 100).toFixed(digits);
  if (signed && n > 0) return `+${formatted}%`;
  return `${formatted}%`;
}

export function formatCompact(n: number): string {
  return new Intl.NumberFormat("en-US", { notation: "compact", maximumFractionDigits: 2 }).format(n);
}

/**
 * Format a raw numeric input string with thousands separators as the
 * user types — "1234567.8" → "1,234,567.8". Preserves a trailing dot
 * and an in-progress decimal part so it's safe to apply on every
 * keystroke. Pair with `stripNumeric` to read the raw value back.
 */
export function formatNumericInput(raw: string): string {
  if (!raw) return "";
  const neg = raw.startsWith("-") ? "-" : "";
  const body = neg ? raw.slice(1) : raw;
  const [intPart = "", decPart] = body.split(".");
  const grouped = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return neg + (decPart !== undefined ? `${grouped}.${decPart}` : grouped);
}

/** Strip thousands separators from a formatted numeric string. */
export function stripNumeric(formatted: string): string {
  return formatted.replace(/,/g, "");
}

/** Relative "N min ago" / "3 hours ago" formatter. */
export function formatRelative(iso: string | Date): string {
  const d = typeof iso === "string" ? new Date(iso) : iso;
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}
